import { z } from "zod";
import type { I18nJsonb } from "@/server/platform/db/schema";
import {
  FilterConditionSchema,
  OffsetPageQuerySchema,
} from "@/shared/contracts/base";

const jsonFilters = z.preprocess((v) => {
  if (typeof v === "string") {
    try {
      return JSON.parse(v);
    } catch {
      return v;
    }
  }
  return v;
}, z.array(FilterConditionSchema).optional());

/** Query สำหรับ GET /inventory/stock/summary — รับแค่ filters เหมือน list */
export const StockSummaryQuerySchema = z.object({
  filters: jsonFilters.optional(),
});

export type StockSummaryQueryInput = z.infer<typeof StockSummaryQuerySchema>;

export const StockListCursorQuerySchema = z.object({
  limit: z.coerce.number().int().min(1).max(100).default(40),
  after: z.string().optional(),
  filters: jsonFilters.optional(),
});

export type StockListCursorQueryInput = z.infer<
  typeof StockListCursorQuerySchema
>;

export const StockSummaryResponseSchema = z.object({
  totalQuantity: z.number().int().min(0),
  totalCostValue: z.number().int().min(0),
  totalRetailValue: z.number().int().min(0),
});

export type StockSummaryDTO = z.infer<typeof StockSummaryResponseSchema>;

// Movement Types Enum
export const StockMovementTypeEnum = z.enum([
  "adjustment",
  "sale",
  "return",
  "restock",
]);
export type StockMovementType = z.infer<typeof StockMovementTypeEnum>;

// Parameter Schemas
export const VariantIdParamSchema = z.object({
  variantId: z.string().min(1),
});

// Stock Adjustment Schema
// - adjustment: quantityDelta = การเพิ่ม/ลบจากจำนวนปัจจุบัน
// - restock: quantity = จำนวนใหม่ที่ตั้งโดยตรง (ไม่ใช่ delta)
// - createdBy: ใส่จาก server จาก session (client ส่งมาไม่ใช้)
export const StockAdjustmentSchema = z
  .object({
    variantId: z.string().min(1, "Variant ID is required"),
    type: z.enum(["adjustment", "restock"], {
      message: "Type must be adjustment or restock",
    }),
    quantityDelta: z.number().optional(),
    quantity: z.number().int().min(0).optional(),
    note: z.string().optional(),
    createdBy: z.string().optional(),
  })
  .superRefine((data, ctx) => {
    if (data.type === "adjustment") {
      if (data.quantityDelta === undefined || data.quantityDelta === null) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "quantityDelta required for adjustment",
          path: ["quantityDelta"],
        });
      } else if (data.quantityDelta === 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Quantity delta cannot be zero",
          path: ["quantityDelta"],
        });
      }
    }
    if (data.type === "restock") {
      if (data.quantity === undefined || data.quantity === null) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "quantity required for restock",
          path: ["quantity"],
        });
      }
    }
  });

// Stock Threshold Update Schema
export const StockThresholdUpdateSchema = z
  .object({
    variantId: z.string().min(1, "Variant ID is required"),
    lowStockThreshold: z.number().int().min(0).nullable().optional(),
  })
  .refine((obj) => Object.keys(obj).length > 1, {
    message: "No fields to update",
  });

// Stock Movement Query Schema
export const StockMovementQuerySchema = OffsetPageQuerySchema.extend({
  variantId: z.string().optional(),
  type: z.enum(["adjustment", "sale", "return", "restock"]).optional(),
  dateFrom: z.string().datetime().optional(),
  dateTo: z.string().datetime().optional(),
});

// Input Types
export type StockAdjustmentInput = z.infer<typeof StockAdjustmentSchema>;
export type StockThresholdUpdateInput = z.infer<
  typeof StockThresholdUpdateSchema
>;
export type StockMovementQueryInput = z.infer<typeof StockMovementQuerySchema>;

// DTO Interfaces
export interface StockDTO {
  variantId: string;
  quantity: number;
  lowStockThreshold: number | null;
  updatedAt: string;
}

export interface StockMovementDTO {
  id: string;
  variantId: string;
  quantityDelta: number;
  type: StockMovementType;
  refId: string | null;
  note: string | null;
  createdBy: string | null;
  createdAt: string;
}

export interface ProductBasicDTO {
  id: string;
  name: I18nJsonb | null;
  /** รูปแรกของสินค้า (ตาม product_image.sort_order) */
  thumbnailFileKey?: string | null;
}

export interface VariantWithStockDTO extends StockDTO {
  productId: string;
  product: ProductBasicDTO;
  sku: string;
  price: number;
  cost: number;
  optionValues: Record<string, string> | null;
  optionValuesDetails?: Array<{
    optionName: { lo?: string; th?: string; en?: string } | null;
    valueName: { lo?: string; th?: string; en?: string } | null;
  }>;
}

/** POS / catalog — หนึ่งสินค้าพร้อม variants ที่มีสต็อกในหน้านั้น */
export interface StockProductGroupDTO {
  productId: string;
  product: ProductBasicDTO;
  variants: VariantWithStockDTO[];
}

// Stock Status Types
export type StockStatus = "out_of_stock" | "low_stock" | "ok";

export function getStockStatus(
  quantity: number,
  threshold: number | null,
): StockStatus {
  if (quantity === 0) return "out_of_stock";
  if (threshold !== null && quantity <= threshold) return "low_stock";
  return "ok";
}
